import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowLeft, Droplets } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { MEMBERSHIP_PLANS, BUSINESS } from "../data";

export default function MembershipPage() {
  const navigate = useNavigate();
  const handleBook = () => navigate("/#booking");

  return (
    <div className="dark grain bg-[#050505] min-h-screen" data-testid="membership-page">
      <Navbar onBook={handleBook} />

      <section className="relative pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <button
            data-testid="membership-back"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors mb-12"
          >
            <ArrowLeft size={14} /> Back
          </button>

          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 mb-7"
            >
              <Droplets size={14} className="text-white" />
              <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-300">
                Maintenance Washes · Monthly Plans
              </span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05 }}
              className="font-heading text-5xl sm:text-6xl font-extrabold tracking-tighter text-white leading-[0.95]"
            >
              Membership
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="mt-6 text-base md:text-lg text-zinc-400 leading-relaxed max-w-xl"
            >
              Keep your vehicle immaculate all year with recurring maintenance washes.
              Choose a plan and we handle the rest.
            </motion.p>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-start">
            {MEMBERSHIP_PLANS.map((plan, i) => (
              <motion.div
                key={plan.name}
                data-testid={`plan-${plan.num}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.1 }}
                className={`relative rounded-3xl p-8 md:p-9 flex flex-col ${
                  plan.popular
                    ? "border border-white/40 bg-white/[0.04]"
                    : "border border-white/10 bg-[#111111]"
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-8 rounded-full bg-[#fafafa] text-[#050505] font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1">
                    Most Popular
                  </span>
                )}
                <span className="font-mono text-xs text-zinc-600">{plan.num}</span>
                <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight text-white uppercase">
                  {plan.name}
                </h2>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-heading text-5xl font-extrabold tracking-tighter text-white">
                    {plan.price}
                  </span>
                  <span className="font-mono text-sm text-zinc-500 mb-1.5">/month</span>
                </div>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                  {plan.cadence}
                </p>

                <ul className="mt-7 space-y-3.5 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
                      <Check size={17} className="shrink-0 mt-0.5 text-white" />
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`plan-book-${plan.num}`}
                  onClick={handleBook}
                  className={`btn-3d gloss-sweep mt-8 w-full rounded-full font-semibold px-6 py-3.5 ${
                    plan.popular
                      ? "bg-[#fafafa] text-[#050505] hover:bg-white"
                      : "bg-[#fafafa] text-[#050505] hover:bg-white"
                  }`}
                >
                  Book Now
                </button>
              </motion.div>
            ))}
          </div>

          <p className="mt-10 text-sm text-zinc-500">
            Prices may vary depending on vehicle size and condition. Questions? Call or
            text us at{" "}
            <a href={`tel:${BUSINESS.phoneRaw}`} className="text-white underline underline-offset-4">
              {BUSINESS.phone}
            </a>
            .
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
